import { useState, useCallback } from 'react';
import { fiscalYearsApi } from '../services/api/endpoints/fiscalYears';
import type { 
  TransitionCheck, 
  TransitionCheckResult, 
  CreateNextFiscalYearParams, 
  TransitionCreateResult 
} from '../types/fiscal-year';

export interface TransitionState {
  canTransition: boolean;
  checks: TransitionCheck[];
  previousFiscalYear: {
    id: string;
    code: string;
    status: 'open' | 'closed' | 'audited';
    startDate: string;
    endDate: string;
  } | null;
}

export interface TransitionResult {
  success: boolean;
  message: string;
  newFiscalYearId?: string;
  closingEntriesCount?: number;
  openingEntriesCount?: number;
  balances?: {
    assets: number;
    liabilities: number;
    equity: number;
    result: number;
  };
}

/**
 * Hook pour gérer la transition entre exercices fiscaux
 * 
 * Permet de:
 * - Vérifier si une transition est possible
 * - Créer un nouvel exercice avec report automatique des soldes
 */
export function useFiscalYearTransition() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transitionState, setTransitionState] = useState<TransitionState>({
    canTransition: false,
    checks: [],
    previousFiscalYear: null
  });

  /**
   * Vérifier si une transition vers un nouvel exercice est possible
   * @param previousFiscalYearId - ID de l'exercice précédent
   */
  const checkTransition = useCallback(async (previousFiscalYearId: string): Promise<TransitionState> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fiscalYearsApi.checkTransition(previousFiscalYearId);
      
      if (response.success && response.data) {
        const state: TransitionState = {
          canTransition: response.data.canTransition,
          checks: response.data.checks || [],
          previousFiscalYear: response.data.previousFiscalYear
        };
        setTransitionState(state);
        return state;
      } else {
        const errorMessage = (response as any).error || 'Erreur lors de la vérification de transition';
        setError(errorMessage);
        return {
          canTransition: false,
          checks: [],
          previousFiscalYear: null
        };
      }
    } catch (err) {
      console.error('Error checking transition:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la vérification';
      setError(errorMessage);
      return {
        canTransition: false,
        checks: [],
        previousFiscalYear: null
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Créer un nouvel exercice fiscal avec report automatique des soldes
   * 
   * Génère automatiquement:
   * - Les écritures de clôture (Classes 6, 7 → Compte 13)
   * - Les écritures d'ouverture (Report classes 1-5)
   */
  const createNextFiscalYear = useCallback(async (params: CreateNextFiscalYearParams): Promise<TransitionResult> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fiscalYearsApi.createNextWithTransition(params);
      
      if (response.success && response.data?.success) {
        return {
          success: true,
          message: response.data.message,
          newFiscalYearId: response.data.newFiscalYearId,
          closingEntriesCount: response.data.closingEntriesCount,
          openingEntriesCount: response.data.openingEntriesCount,
          balances: response.data.balances
        };
      } else {
        const errorMessage = response.error || (response.data as any)?.message || 'Erreur lors de la création';
        setError(errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      }
    } catch (err) {
      console.error('Error creating next fiscal year:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la création du nouvel exercice';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Réinitialiser l'état de transition
   */
  const resetTransitionState = useCallback(() => {
    setTransitionState({
      canTransition: false,
      checks: [],
      previousFiscalYear: null
    });
    setError(null);
  }, []);

  /**
   * Vérifie si tous les checks critiques (severity: 'error') sont passés
   */
  const hasBlockingErrors = useCallback(() => {
    return transitionState.checks.some(check => check.severity === 'error' && !check.passed);
  }, [transitionState.checks]);

  /**
   * Vérifie si il y a des avertissements (severity: 'warning')
   */
  const hasWarnings = useCallback(() => {
    return transitionState.checks.some(check => check.severity === 'warning' && !check.passed);
  }, [transitionState.checks]);

  return {
    // État
    loading,
    error,
    transitionState,
    
    // Actions
    checkTransition,
    createNextFiscalYear,
    resetTransitionState,
    
    // Helpers
    hasBlockingErrors,
    hasWarnings
  };
}
